import {
  isBaseBlueprint,
  type CustomSandboxSize,
  type ResourceSize,
} from '@runloop/reflex-contract';
import { type Blueprint } from '@reflex/shared';

/**
 * The devbox size a launch should use, layering an explicit user pick over the
 * size baked into the selected blueprint.
 *
 * A user pick (a named tier OR a custom size) always wins wholesale. Otherwise
 * a non-base blueprint's baked size is inherited so "if a blueprint has a size,
 * use that size" — the same size the {@link DevboxSizeChip} shows as coming
 * "from &lt;blueprint&gt;". Base blueprints are substrates whose build size is
 * not a launch intent, so they never contribute a size (inheriting one would
 * silently shrink the usual default launches). Trials are size-capped and keep
 * the trial default rather than a (possibly larger) blueprint size — mirroring
 * the chip, which does not show inheritance under a trial. Keeps the launch
 * form and dialog agreeing on one rule.
 */
export function resolveEffectiveSandboxSize(opts: {
  resourceSize: ResourceSize | null;
  customSize: CustomSandboxSize | null;
  blueprint: Blueprint | null;
  isTrial?: boolean;
}): { resourceSize: ResourceSize | null; customSize: CustomSandboxSize | null } {
  const userPicked = opts.resourceSize != null || opts.customSize != null;
  if (userPicked || opts.isTrial) {
    return { resourceSize: opts.resourceSize, customSize: opts.customSize };
  }
  const bp = opts.blueprint && !isBaseBlueprint(opts.blueprint) ? opts.blueprint : null;
  return {
    resourceSize: bp?.resourceSize ?? null,
    customSize: bp?.customSize ?? null,
  };
}
